import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { Loader, Button, CurrencyValue } from '@jutro/components';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { withRouter } from 'react-router-dom';
import { WniPremiumReportService } from 'wnice-capability-premiumreport';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';

import { getCostsData } from '../../utils/WCPremiumReportUtil';
import metadata from './CEWCPremiumReportPremiumsPage.metadata.json5';
import styles from './CEWCPremiumReportPremiumsPage.module.scss';
import commonStyles from '../../CEWCPremiumReportWizard.module.scss';
import messages from './CEWCPremiumReportPremiumsPage.messages';


function CEWCPremiumReportPremiumsPage(props) {
    const { wizardData, updateWizardData, wizardSnapshot, goPrevious, steps, currentStepIndex, authHeader, history } = props;
    const { onValidate } = useValidation('CEWCPremiumReportPremiumsPage');
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);

    const {
        details: premiumReportDetailsData,
        summary : { taxAndSurcharges, total, comments },
        jobNumber,
        policyNumber,
        accountNumber,
        jobStatus,
        sessionUUID
    } = wizardData.value;
    const isAuditComplete = jobStatus === 'AuditComplete';


    const costsData = getCostsData(premiumReportDetailsData, total);

    const onSave = useCallback(async () => {
        if (comments !== _.get(wizardSnapshot, 'value.summary.comments')) {
            setIsLoading(true);
            await WniPremiumReportService.savePremiumReport(
                jobNumber, [], comments, sessionUUID,
                authHeader
            );
            setIsLoading(false);
        }
    }, [authHeader, comments, jobNumber, sessionUUID, wizardSnapshot]);

    const onCancel = useCallback(() => {
        onSave();
        history.push('/premium-reports');
    }, [history, onSave]);

    const onPrevious = useCallback(() => {
        // jump to Details page
       // jumpTo(0);
       _.set(steps, `${currentStepIndex}.visited`, true);
       goPrevious();
    }, [currentStepIndex, goPrevious, steps]);

    const onEdit = useCallback(async () => {
        setIsLoading(true);
        wizardData.value = await WniPremiumReportService.editPremiumReport(
            jobNumber, comments, sessionUUID,
            authHeader
        );
        setIsLoading(false);
        // jump to Details page
        _.set(steps, `${currentStepIndex}.visited`, false);
        goPrevious();
    }, [wizardData, jobNumber, comments, sessionUUID, authHeader, steps, currentStepIndex, goPrevious]);

    const onSubmit = useCallback(async () => {
        setIsLoading(true);
        await WniPremiumReportService.submitPremiumReport(
            jobNumber, costsData, comments, sessionUUID,
            authHeader
        );
        setIsLoading(false);
        // jump to the confirmation screen
        history.push('/premium-report-success', {
            jobNumber: jobNumber,
            policyNumber: policyNumber,
            accountNumber: accountNumber
        });
    }, [accountNumber, authHeader, comments, costsData, history, jobNumber, policyNumber, sessionUUID]);

    const renderCustomWizardPageFooterContent = useCallback(() => {
        return (
            <>
                <Button
                    id="gw-wizard-cancel"
                    disabled={isAuditComplete}
                    onClick={onCancel}
                    variant="secondary" 
                    label={translator(messages.cancelBtn)}
                />
                <Button
                    id="gw-wizard-previous"
                    onClick={onPrevious}
                    variant="secondary" 
                    label={translator(messages.previousBtn)}
                />
                <Button
                    id="gw-wizard-edit"
                    disabled={isAuditComplete}
                    onClick={onEdit}
                    variant="secondary" 
                    label={translator(messages.editBtn)}
                />
                <Button
                    id="gw-wizard-save"
                    disabled={isAuditComplete}
                    onClick={onSave}
                    variant="secondary" 
                    label={translator(messages.saveBtn)}
                />
                <Button
                    id="gw-wizard-submit"
                    disabled={isAuditComplete}
                    onClick={onSubmit}
                    variant="primary" 
                    label={translator(messages.submitBtn)}
                />
            </>
        );
    }, [isAuditComplete, onCancel, onEdit, onPrevious, onSave, onSubmit, translator]);

    const renderCell = (item, index, property) => {
        if (property.id === 'auditedBasis') {
            const currencyValue = {
                amount: item[property.id],
                currency: 'USD'
            }
            return (
                <CurrencyValue
                    id={`actual_${index}`}
                    value={currencyValue}
                    showFractions={false}
                />
            );
        }
        return item[property.id];
     };

    const renderPremiumCell = (item, index, property) => {
        return (
            <CurrencyValue
                id={`currency_${index}`}
                value={item[property.id]}
                showFractions
            />
        );
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            renderCell,
            renderPremiumCell
        } 
    };

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: false,
            phoneWide: {
                labelPosition: 'top'
            }
        },
        premiumsHeader: {
            className: commonStyles.premiumReportWizardTitle
        },
        taxAndSurcharges: {
            visible: taxAndSurcharges.amount > 0
        },
        noTaxAndSurcharges: {
            visible: taxAndSurcharges.amount === 0,
            value: translator(messages.noTaxAndSurcharge)
        },
        premiumReportTable: {
            data: costsData
        },
        comments: {
            readOnly: isAuditComplete
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <WizardPage
            renderCustomWizardPageFooterContent = {renderCustomWizardPageFooterContent}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

CEWCPremiumReportPremiumsPage.propTypes = wizardProps;
export default withAuthenticationContext(withRouter(CEWCPremiumReportPremiumsPage));
